
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Transfer',
  title: 'Transfer Sandbox',
  description: 'Use Sandbox to test your transfer integration'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <Callout mdxType="Callout">
      <p>{`While all newly created teams can use Transfer in Sandbox by default, some older teams may need to be manually enabled. If you receive an error indicating that the product is not enabled for your account when attempting to use Transfer in the Sandbox environment, contact support or your Plaid account manager to request access.`}</p>
    </Callout>
    <h4 {...{
      "id": "introduction-to-sandbox"
    }}>{`Introduction to Sandbox`}</h4>
    <p>{`Sandbox is Plaid's environment for testing using fake data. For more details about getting started with Sandbox environment, including how to skip the Link flow in Sandbox with `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{`, and testing non-Transfer-specific functionality such as Item errors, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/"
      }}>{`Sandbox`}</a>{`.`}</p>
    <h4 {...{
      "id": "simulating-money-movement-events-and-webhooks"
    }}>{`Simulating money movement, events, and webhooks`}</h4>
    <p>{`When creating transfers in Sandbox, see the `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{` documentation on how to generate approved or declined authorization responses.`}</p>
    <p>{`Any cutoff times applied in Production will also apply in the Sandbox environment. For example, creating a same-day ACH transfer in Sandbox after the `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#ach-processing-windows"
      }}>{`Same Day ACH cutoff`}</a>{` will automatically update the transfer to standard ACH.`}</p>
    <p>{`In the Sandbox environment, no real banks are involved and no events are triggered automatically. By default, all transfers, refunds, and sweeps created in Sandbox remain at the `}<inlineCode parentName="p">{`pending`}</inlineCode>{` status until you actively change them. This can be most easily accomplished through the Plaid Dashboard, where you can simulate next steps and common failure scenarios for any transfer that you've created in the Sandbox environment.`}</p>
    <Image src='/assets/img/docs/transfer/sandbox_dashboard.png' alt="Plaid Dashboard UI: Simulate dropdown menu to simulate sandbox transfer events. Options: 'Next event,' 'Failure,' and 'Return.'" isLazy={false} caption='You can simulate many Sandbox events using the Plaid Dashboard.' mdxType="Image" />
    <p>{`If you would like to change the status of a transfer through the API, you can call the following endpoints to simulate events and fire transfer webhooks manually.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Action to simulate`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Simulation endpoint`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Transfer events`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/transfer/simulate`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Refund events`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/transfer/refund/simulate`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Ledger withdrawals sweep events`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/transfer/ledger/withdraw/simulate`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Ledger deposits sweep events`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/transfer/ledger/deposit/simulate`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Ledger pending-to-available funds movements`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/transfer/ledger/simulate_available`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Webhooks`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/transfer/fire_webhook`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p>{`When you change the status of a Sandbox transfer through the Dashboard, the corresponding webhook will not fire. Through the API, the simulate endpoints fire a webhook only if you pass the optional `}<inlineCode parentName="p">{`webhook`}</inlineCode>{` field. Otherwise, use the `}<inlineCode parentName="p">{`/sandbox/transfer/fire_webhook`}</inlineCode>{` API to send a Transfer webhook in Sandbox.`}</p>
    <p>{`For a full list of transfer simulations available in Sandbox, see `}<a parentName="p" {...{
        "href": "/docs/api/sandbox/"
      }}>{`Sandbox endpoints`}</a>{`.`}</p>
    <p>{`For a general overview of the Plaid Sandbox environment, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/"
      }}>{`Sandbox overview`}</a>{`.`}</p>
    <h4 {...{
      "id": "automatic-sandbox-simulations"
    }}>{`Automatic Sandbox simulations`}</h4>
    <p>{`In addition to manual simulations, the Sandbox environment supports automatic state transitions for transfers, refunds, and sweeps (deposits and withdrawals) when using specific test amounts. These special amounts trigger predefined state transitions without requiring manual intervention through the Dashboard or API.`}</p>
    <p>{`Once you initiate a transfer with any of these specific amount values, the transfer's stages will be immediately simulated. The appropriate webhooks for Sandbox transfer events will be fired, and you can fetch the events through `}<inlineCode parentName="p">{`/transfer/event/sync`}</inlineCode>{`. You can also view the events' progression on your Transfer Dashboard, on the details page for the specific transfer.`}</p>
    <Callout mdxType="Callout">
      <p>{`In Production, transfer state changes can take minutes to hours depending on processing time. Sandbox simulations provide immediate state transitions for testing purposes.`}</p>
    </Callout>
    <h5 {...{
      "id": "transfer-state-transitions"
    }}>{`Transfer state transitions`}</h5>
    <p>{`Creating Sandbox transfers with the specified amounts via `}<inlineCode parentName="p">{`/transfer/create`}</inlineCode>{` will result in automatic state transitions.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Amount`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`State Transition Path`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$11.11`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → posted → settled → funds available (ACH debits only)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$22.22`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → failed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$33.33`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → posted → settled → funds available (ACH debits only) → returned (R01)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$44.44`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → posted → settled → funds available (ACH debits only) → returned (R02)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$55.55`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → posted → settled → funds available (ACH debits only) → returned (R16)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$66.66`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → posted → settled → returned`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "refund-state-transitions"
    }}>{`Refund state transitions`}</h5>
    <p>{`Creating Sandbox refunds with the specified amounts via `}<inlineCode parentName="p">{`/transfer/refund/create`}</inlineCode>{` will result in automatic state transitions.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Amount`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`State Transition Path`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$1.11`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → posted → settled → returned`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$2.22`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → failed`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "sweep-state-transitions"
    }}>{`Sweep state transitions`}</h5>
    <p>{`Creating Sandbox sweeps with the specified amounts via `}<inlineCode parentName="p">{`/transfer/ledger/deposit`}</inlineCode>{` and `}<inlineCode parentName="p">{`/transfer/ledger/withdraw`}</inlineCode>{` will result in automatic state transitions.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Amount`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`State Transition Path`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$111.11`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → posted → settled → funds available (deposits only)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$222.22`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → failed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$333.33`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → posted → settled → funds available (deposits only) → returned`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`$444.44`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`pending → posted → settled → returned`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "testing-plaid-ledger-flow-of-funds"
    }}>{`Testing Plaid Ledger flow of funds`}</h4>
    <p>{`While testing Plaid Ledger in Sandbox, you can always call the following endpoints at any time to verify expected behavior:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`/transfer/get`}</inlineCode>{` to validate the transfer status change`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`/transfer/sweep/get`}</inlineCode>{` to validate the deposit/withdrawal status change`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`/transfer/ledger/get`}</inlineCode>{` to validate the Ledger balance change`}</li>
    </ul>
    <h4 {...{
      "id": "wire-transfers"
    }}>{`Wire transfers`}</h4>
    <p>{`In Sandbox, all customers are enabled for wire transfers in order to facilitate testing. Having access to wire transfers in Sandbox does not mean you are enabled for wires in Production. To request access to wire transfers in Production, contact your account manager.`}</p>
    <h5 {...{
      "id": "issuing-payouts-with-a-plaid-ledger"
    }}>{`Issuing payouts with a Plaid Ledger`}</h5>
    <h6 {...{
      "id": "adding-funds-to-the-plaid-ledger"
    }}>{`Adding funds to the Plaid Ledger`}</h6>
    <p>{`All new Plaid Ledgers have a starting balance of $100 in Sandbox. Once this is depleted, or if you wish to simulate a larger transfer, you will need to fund the Ledger before testing the payout.`}</p>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/transfer/ledger/deposit`}</inlineCode>{` to create a sweep that adds funds to a Plaid Ledger balance. The funds will immediately show up in the `}<inlineCode parentName="li">{`pending`}</inlineCode>{` balance.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/transfer/ledger/deposit/simulate`}</inlineCode>{` with `}<inlineCode parentName="li">{`sweep.posted`}</inlineCode>{` as the `}<inlineCode parentName="li">{`event_type`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/transfer/ledger/deposit/simulate`}</inlineCode>{` with `}<inlineCode parentName="li">{`sweep.settled`}</inlineCode>{` as the `}<inlineCode parentName="li">{`event_type`}</inlineCode>{`. This will move your sweep to `}<inlineCode parentName="li">{`settled`}</inlineCode>{` status.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/transfer/ledger/simulate_available`}</inlineCode>{` in order to simulate the passage of time and transition the funds from `}<inlineCode parentName="li">{`pending`}</inlineCode>{` to `}<inlineCode parentName="li">{`available`}</inlineCode>{`.`}</li>
    </ol>
    <h6 {...{
      "id": "issuing-a-payout"
    }}>{`Issuing a payout`}</h6>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/transfer/authorization/create`}</inlineCode>{` with `}<inlineCode parentName="li">{`type=credit`}</inlineCode>{` and your desired network. To simulate an insufficient funds failure, call `}<inlineCode parentName="li">{`/transfer/authorization/create`}</inlineCode>{` with an amount larger than your current available balance.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/transfer/create`}</inlineCode>{`. This will immediately create a transfer and decrement the available balance.`}</li>
    </ol>
    <h5 {...{
      "id": "receiving-payments-via-plaid-ledger"
    }}>{`Receiving payments via Plaid Ledger`}</h5>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/transfer/authorization/create`}</inlineCode>{` with `}<inlineCode parentName="li">{`type=debit`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/transfer/create`}</inlineCode>{`. This will immediately create a transfer and increment your pending balance in the Plaid Ledger.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/transfer/simulate`}</inlineCode>{` with `}<inlineCode parentName="li">{`posted`}</inlineCode>{` as the `}<inlineCode parentName="li">{`event_type`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/transfer/simulate`}</inlineCode>{` with `}<inlineCode parentName="li">{`settled`}</inlineCode>{` as the `}<inlineCode parentName="li">{`event_type`}</inlineCode>{`. This will move your transfer to `}<inlineCode parentName="li">{`settled`}</inlineCode>{` status. The funds will remain in `}<inlineCode parentName="li">{`pending`}</inlineCode>{` until the hold is elapsed.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/transfer/ledger/simulate_available`}</inlineCode>{` to simulate the passage of time and convert the Ledger balance to `}<inlineCode parentName="li">{`available`}</inlineCode>{`. If you are a Transfer for Platforms customer, this will convert all pending balances in all Plaid Ledgers.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/transfer/ledger/withdraw`}</inlineCode>{` to create a sweep that withdraws funds from the Plaid Ledger balance. The endpoint will immediately decrement the available balance and create a sweep with `}<inlineCode parentName="li">{`pending`}</inlineCode>{` status.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/transfer/ledger/withdraw/simulate`}</inlineCode>{` with `}<inlineCode parentName="li">{`sweep.posted`}</inlineCode>{` as the `}<inlineCode parentName="li">{`event_type`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/transfer/ledger/withdraw/simulate`}</inlineCode>{` with `}<inlineCode parentName="li">{`sweep.settled`}</inlineCode>{` as the `}<inlineCode parentName="li">{`event_type`}</inlineCode>{`. This will move your sweep to `}<inlineCode parentName="li">{`settled`}</inlineCode>{` status.`}</li>
    </ol>
    <p>{`In Production, transfer and sweep status will be updated automatically to reflect the real processing status, and the `}<inlineCode parentName="p">{`pending`}</inlineCode>{` balance will automatically become `}<inlineCode parentName="p">{`available`}</inlineCode>{` after the client-specific hold day.`}</p>
    <h5 {...{
      "id": "simulating-a-return-with-plaid-ledger"
    }}>{`Simulating a return with Plaid Ledger`}</h5>
    <ol>
      <li parentName="ol">{`Follow steps 1-3 in `}<a parentName="li" {...{
          "href": "#receiving-payments-via-plaid-ledger"
        }}>{`Receiving payments via Plaid Ledger`}</a>{` section to create a debit and move it to `}<inlineCode parentName="li">{`posted`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`(Optional) To simulate a return after the funds were made available, follow steps 4-5 as well. If you would like to simulate a return before the funds were made available, omit this step.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/transfer/simulate`}</inlineCode>{` with `}<inlineCode parentName="li">{`returned`}</inlineCode>{` as the `}<inlineCode parentName="li">{`event_type`}</inlineCode>{`. This will move the transfer status to returned and decrement pending balance, which you can verify by calling `}<inlineCode parentName="li">{`/transfer/ledger/get`}</inlineCode>{`.`}</li>
    </ol>
    <h6 {...{
      "id": "testing-instant-payouts"
    }}>{`Testing Instant Payouts`}</h6>
    <p>{`Testing Instant Payouts works the same way as testing ACH Transfers.`}</p>
    <p>{`To test the `}<inlineCode parentName="p">{`/transfer/capabilities/get`}</inlineCode>{` endpoint in Sandbox, log in using the `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` user Sandbox account (see `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials/#sandbox-simple-test-credentials"
      }}>{`Sandbox simple test credentials`}</a>{` for more information), and use any depository account (such as checking or savings) in the "First Platypus Bank" institution, which will return `}<inlineCode parentName="p">{`true`}</inlineCode>{`. Non-depository accounts, such as the CD account, will return an `}<inlineCode parentName="p">{`INVALID_ACCOUNT_ID`}</inlineCode>{` error. If using a `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/"
      }}>{`custom Sandbox user`}</a>{`, set `}<inlineCode parentName="p">{`numbers.ach_routing`}</inlineCode>{` to `}<inlineCode parentName="p">{`322271627`}</inlineCode>{` in order to return `}<inlineCode parentName="p">{`true`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "setting-balances"
    }}>{`Setting balances`}</h4>
    <p>{`You can customize the starting balance and certain other data for a linked account by using a Sandbox `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/"
      }}>{`custom user`}</a>{`. The easiest way to set a custom balance is to use a test user in the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/sandbox-custom-users/"
      }}>{`custom users repo`}</a>{`. Using one of the example files, modify the starting balance, then follow the instructions in the repo's README to add the user to your Sandbox.`}</p>
    <h4 {...{
      "id": "simulating-recurring-transfers"
    }}>{`Simulating recurring transfers`}</h4>
    <p>{`In the Sandbox environment, recurring transfers can be simulated by using a `}<inlineCode parentName="p">{`test_clock`}</inlineCode>{` object.`}</p>
    <p>{`A `}<inlineCode parentName="p">{`test_clock`}</inlineCode>{` is a mock clock that has a `}<inlineCode parentName="p">{`virtual_time`}</inlineCode>{` field, indicating the current timestamp on this test clock. You can attach a `}<inlineCode parentName="p">{`test_clock`}</inlineCode>{` to a `}<inlineCode parentName="p">{`recurring_transfer`}</inlineCode>{` in Sandbox by providing a `}<inlineCode parentName="p">{`test_clock_id`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/transfer/recurring/create`}</inlineCode>{`.`}</p>
    <p>{`You can advance the `}<inlineCode parentName="p">{`virtual_time`}</inlineCode>{` on a `}<inlineCode parentName="p">{`test_clock`}</inlineCode>{` to a higher value by calling `}<inlineCode parentName="p">{`/sandbox/transfer/test_clock/advance`}</inlineCode>{`, but you can never decrease the `}<inlineCode parentName="p">{`virtual_time`}</inlineCode>{`.`}</p>
    <p>{`When a test clock is advanced, all active recurring transfers attached to this clock will generate new originations as if the time had elapsed in Production. For instance, assuming a test clock is attached to a weekly recurring transfer, if the test clock is advanced by 14 days, you should see two new transfers being created.`}</p>
    <p>{`Note that advancing a test clock does not change the status of the transfer objects created by a recurring transfer. Transfers will stay in `}<inlineCode parentName="p">{`pending`}</inlineCode>{` status unless you call `}<inlineCode parentName="p">{`/sandbox/transfer/simulate`}</inlineCode>{` to simulate a transfer status update.`}</p>
    <h5 {...{
      "id": "sample-sandbox-recurring-transfer-scenarios"
    }}>{`Sample Sandbox recurring transfer scenarios`}</h5>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Create a test clock using `}<inlineCode parentName="p">{`/sandbox/transfer/test_clock/create`}</inlineCode>{`, with a `}<inlineCode parentName="p">{`virtual_time`}</inlineCode>{` of `}<inlineCode parentName="p">{`"2022-11-14T07:00:00-08:00"`}</inlineCode>{`, which is 2022-11-14 7AM PST (Monday).`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Create a weekly recurring transfer on every Tuesday using `}<inlineCode parentName="p">{`/transfer/recurring/create`}</inlineCode>{` with the `}<inlineCode parentName="p">{`test_clock_id`}</inlineCode>{` returned from step 1. The recurring schedule starts on 2022-11-15, and ends on 2022-11-30.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Advance the test clock to `}<inlineCode parentName="p">{`"2022-11-15T23:59:00-08:00"`}</inlineCode>{`, which is the end of day 2022-11-15 PST (Tuesday).`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Since we advanced the test clock to the last minute of Tuesday and the recurring transfer is also scheduled on every Tuesday, we expect to see 1 transfer being created. Inspect the recurring transfer created in step 2 with `}<inlineCode parentName="p">{`/transfer/recurring/get`}</inlineCode>{`. Confirm the `}<inlineCode parentName="p">{`transfer_ids`}</inlineCode>{` field now has 1 element, and the `}<inlineCode parentName="p">{`status`}</inlineCode>{` field is `}<inlineCode parentName="p">{`"active"`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Advance the test clock to `}<inlineCode parentName="p">{`"2022-11-29T23:59:00-08:00"`}</inlineCode>{`, which is the end of day 2022-11-29 PST (Tuesday).`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Inspect the recurring transfer created in step 2 with `}<inlineCode parentName="p">{`/transfer/recurring/get`}</inlineCode>{`. Confirm that the `}<inlineCode parentName="p">{`transfer_ids`}</inlineCode>{` field now has 3 elements, and the `}<inlineCode parentName="p">{`status`}</inlineCode>{` field is now `}<inlineCode parentName="p">{`"expired"`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Advance the test clock to `}<inlineCode parentName="p">{`"2022-12-06T23:59:00-08:00"`}</inlineCode>{`, which is the end of day 2022-12-06 PST (Tuesday).`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Inspect the recurring transfer created in step 2 with `}<inlineCode parentName="p">{`/transfer/recurring/get`}</inlineCode>{`. Confirm that the `}<inlineCode parentName="p">{`transfer_ids`}</inlineCode>{` field still has 3 elements, and the status field remains `}<inlineCode parentName="p">{`"expired"`}</inlineCode>{`.`}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "testing-transfer-for-platforms-end-user-onboarding"
    }}>{`Testing Transfer for Platforms end-user onboarding`}</h4>
    <p>{`In Sandbox, `}<a parentName="p" {...{
        "href": "/docs/transfer/platform-payments/"
      }}>{`Transfer for Platforms`}</a>{` customers must use `}<inlineCode parentName="p">{`/transfer/platform/originator/create`}</inlineCode>{` instead of the `}<inlineCode parentName="p">{`/partner/customer/create`}</inlineCode>{` and `}<inlineCode parentName="p">{`/partner/customer/enable`}</inlineCode>{` endpoints that they would use in Production.`}</p>
    <p><inlineCode parentName="p">{`/transfer/platform/originator/create`}</inlineCode>{`, `}<inlineCode parentName="p">{`/transfer/platform/requirement/submit`}</inlineCode>{`, and `}<inlineCode parentName="p">{`/transfer/platform/document/submit`}</inlineCode>{` will all succeed on valid requests. `}<inlineCode parentName="p">{`/transfer/platform/person/create`}</inlineCode>{` will succeed and return a random `}<inlineCode parentName="p">{`person_id`}</inlineCode>{`.`}</p>
    <p>{`In addition to working with `}<inlineCode parentName="p">{`originator_client_id`}</inlineCode>{`s created by `}<inlineCode parentName="p">{`/transfer/platform/originator/create`}</inlineCode>{`, `}<inlineCode parentName="p">{`/transfer/originator/get`}</inlineCode>{` will return the following static responses based on which `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` is passed as the `}<inlineCode parentName="p">{`originator_client_id`}</inlineCode>{`:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="th">{`client_id`}</inlineCode></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Response`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`111111111111111111111111`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`approved`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`222222222222222222222222`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`denied`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`333333333333333333333333`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`under_review`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`444444444444444444444444`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`more_information_required`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "introduction-to-sandbox",
        level: 4,
        title: "Introduction to Sandbox",
      },
{
        id: "simulating-money-movement-events-and-webhooks",
        level: 4,
        title: "Simulating money movement, events, and webhooks",
      },
{
        id: "automatic-sandbox-simulations",
        level: 4,
        title: "Automatic Sandbox simulations",
      },
{
        id: "testing-plaid-ledger-flow-of-funds",
        level: 4,
        title: "Testing Plaid Ledger flow of funds",
      },
{
        id: "wire-transfers",
        level: 4,
        title: "Wire transfers",
      },
{
        id: "setting-balances",
        level: 4,
        title: "Setting balances",
      },
{
        id: "simulating-recurring-transfers",
        level: 4,
        title: "Simulating recurring transfers",
      },
{
        id: "testing-transfer-for-platforms-end-user-onboarding",
        level: 4,
        title: "Testing Transfer for Platforms end-user onboarding",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
